/**
 * KeyMismatchBanner — shown when this device's local encryption key no longer
 * matches what the auth server has on record.
 *
 * This happens when the browser's IndexedDB is cleared (private mode, cache
 * wipe, browser reinstall) while localStorage still contains the device ID.
 * A new key pair is generated locally but the server still has seeds wrapped
 * with the old key — so the device can't decrypt any workspace content.
 *
 * The user must either:
 *  a) Enter their recovery phrase to unwrap seeds independently, or
 *  b) Ask another approved device to re-share seeds via Settings → Devices.
 */

import React from 'react';
import { ShieldAlert, Key } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface KeyMismatchBannerProps {
  /** Called when the user chooses to enter their recovery phrase. */
  onUseRecovery: () => void;
}

export const KeyMismatchBanner: React.FC<KeyMismatchBannerProps> = ({ onUseRecovery }) => {
  const navigate = useNavigate();

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '0.625rem 1rem',
        background: 'color-mix(in srgb, var(--error) 12%, var(--bg))',
        color: '#fca5a5',
        fontSize: '0.875rem',
        borderBottom: '0.125rem solid color-mix(in srgb, var(--error) 40%, transparent)',
        gap: '0.5rem',
        flexWrap: 'wrap',
      }}
      role="alert"
      aria-live="polite"
    >
      <ShieldAlert size={15} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1 }}>
        <strong>Encryption key mismatch.</strong>
        {' '}Your device's keys were regenerated — encrypted content is inaccessible.{' '}
        Use your recovery phrase, or ask another approved device to{' '}
        <span
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => navigate('/devices')}
        >
          re-share keys
        </span>.
      </span>
      <button
        type="button"
        onClick={onUseRecovery}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.25rem',
          background: 'none',
          border: '0.0625rem solid currentColor',
          borderRadius: '0.375rem',
          color: 'inherit',
          fontSize: '0.8125rem',
          padding: '0.25rem 0.625rem',
          cursor: 'pointer',
          flexShrink: 0,
          opacity: 0.85,
        }}
        aria-label="Enter recovery phrase to restore access to encrypted content"
      >
        <Key size={13} />
        Use recovery phrase
      </button>
    </div>
  );
};
